import { useState } from "react";

// Owns every scroll concern of the message list: the floating scroll button and
// the imperative "bring this message into view" calls. Scrolling is a DOM effect,
// not state, so callers invoke it directly instead of setting a flag that a later
// render has to notice and clear.

const SCROLL_TO_TOP = "TO_TOP";
const SCROLL_TO_BOTTOM = "TO_BOTTOM";

// The list is repainted right after the state update that triggered the scroll,
// so the target node only exists on the next frame.
const SCROLL_DELAY = 150;

// Leaves the message clear of the chat header instead of flush against the top.
const HEADER_OFFSET = 70;

const useChatScroll = (listRef) => {

  const [scrollDirection, setScrollDirection] = useState(null);

  const onChatScroll = () => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const maxScroll = scrollHeight - clientHeight;
    // Short lists have nowhere to jump to, so the button stays hidden.
    if (maxScroll <= 400) {
      setScrollDirection(null);
      return;
    }
    setScrollDirection(maxScroll - scrollTop < 500 ? SCROLL_TO_TOP : SCROLL_TO_BOTTOM);
  };

  const onClickScrollBtn = () =>
    listRef.current?.scrollTo({
      top: scrollDirection == SCROLL_TO_TOP ? 0 : listRef.current.scrollHeight,
      behavior: "smooth",
    });

  const scrollToBottom = () =>
    setTimeout(() => listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    }), SCROLL_DELAY);

  // Brings a message that just arrived into view. The lookup is scoped to this
  // list's container: several Chat instances can live on the same page and share
  // message ids, so a document-wide lookup would hit the wrong one. If the message
  // isn't rendered (filtered out, or not stored yet) the bottom is where it will
  // show up anyway.
  const scrollToMessage = (messageId) =>
    setTimeout(() => {
      const list = listRef.current;
      if (!list) return;
      const messageNode = list.querySelector(`#chat-message-${messageId}`);
      list.scrollTo({
        top: messageNode ? messageNode.offsetTop - HEADER_OFFSET : list.scrollHeight,
        behavior: "smooth",
      });
    }, SCROLL_DELAY);

  return {
    scrollDirection,
    scrollToBottom,
    scrollToMessage,
    onChatScroll,
    onClickScrollBtn,
  };
};

export default useChatScroll;
